import canecaCat from '@assets/home2.jpeg';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowRight,
  BookOpen,
  MapPin,
  Sparkles,
  Star,
  Users,
} from 'lucide-react';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import UltimasVagasBadge from '../../ui/UltimasVagasBadge';
import { useNextEditionViewModel } from './useNextEditionViewModel';

const NextEditionView = () => {
  const { evento, livroMes, loading } = useNextEditionViewModel();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const yCard = useTransform(scrollYProgress, [0, 1], [20, -20]);

  if (loading) {
    return (
      <div className="w-full py-20 text-center bg-[#fffafb] text-slate-400 text-xs font-bold uppercase tracking-widest animate-pulse">
        Carregando próxima edição...
      </div>
    );
  }

  if (!evento) {
    return (
      <section className="w-full py-20 bg-[#fffafb] text-center flex flex-col items-center justify-center border-t border-pink-50">
        <div className="w-12 h-12 bg-pink-50 text-[#ff2d6c] rounded-full flex items-center justify-center mb-4 shadow-sm">
          <BookOpen size={20} />
        </div>
        <h3 className="text-xl font-bold text-slate-800">
          Próxima edição em planejamento
        </h3>
        <p className="text-slate-500 text-sm max-w-sm mt-1 px-6 leading-relaxed">
          Nossa curadoria está preparando o próximo livro e espaço ideal.
          Novidades em breve!
        </p>
      </section>
    );
  }

  const title = evento.title || evento.titulo;
  const description = evento.description || evento.descricao;
  const location = evento.location || evento.local;
  const mesNomeBruto = evento.mesNome || 'Próximo Mês';
  const mesNome =
    mesNomeBruto.charAt(0).toUpperCase() + mesNomeBruto.slice(1).toLowerCase();

  // Imagem de fundo: capa do livro do mês se disponível, senão foto padrão
  const imagemFundo = livroMes?.coverBase64 || canecaCat;
  const isLivroCapa = Boolean(livroMes?.coverBase64);

  return (
    <section
      ref={containerRef}
      className="relative w-full py-16 md:py-24 overflow-hidden bg-blush-light"
    >
      {/* BackgroundDecorations */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_55%_at_50%_35%,white,transparent)] opacity-80" />
        <div className="absolute top-1/4 right-[-10%] w-[500px] h-[500px] bg-blush/60 rounded-full blur-[120px]" />

        {/* PaperTexture — grão sutil de papel */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.03] mix-blend-multiply">
          <filter id="paperNoise">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.85"
              numOctaves="2"
              stitchTiles="stitch"
            />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#paperNoise)" />
        </svg>

        {/* LeafBranches — cantos superior-esquerdo e inferior-direito */}
        <svg
          viewBox="0 0 120 200"
          fill="none"
          className="absolute -top-6 -left-6 w-32 h-52 md:w-40 md:h-64 text-petal opacity-20 hidden md:block"
        >
          <path
            d="M10 0 C 40 30, 20 70, 55 90 C 30 100, 15 130, 45 150 C 20 160, 10 185, 35 200"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
          <ellipse
            cx="38"
            cy="55"
            rx="16"
            ry="9"
            transform="rotate(35 38 55)"
            fill="currentColor"
          />
          <ellipse
            cx="52"
            cy="115"
            rx="18"
            ry="10"
            transform="rotate(-25 52 115)"
            fill="currentColor"
          />
          <ellipse
            cx="30"
            cy="170"
            rx="14"
            ry="8"
            transform="rotate(20 30 170)"
            fill="currentColor"
          />
        </svg>

        <svg
          viewBox="0 0 120 200"
          fill="none"
          className="absolute -bottom-10 -right-6 w-32 h-52 md:w-40 md:h-64 text-petal opacity-20 hidden md:block rotate-180"
        >
          <path
            d="M10 0 C 40 30, 20 70, 55 90 C 30 100, 15 130, 45 150 C 20 160, 10 185, 35 200"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
          <ellipse
            cx="38"
            cy="55"
            rx="16"
            ry="9"
            transform="rotate(35 38 55)"
            fill="currentColor"
          />
          <ellipse
            cx="52"
            cy="115"
            rx="18"
            ry="10"
            transform="rotate(-25 52 115)"
            fill="currentColor"
          />
          <ellipse
            cx="30"
            cy="170"
            rx="14"
            ry="8"
            transform="rotate(20 30 170)"
            fill="currentColor"
          />
        </svg>

        <Star
          className="absolute top-16 left-[6%] w-3 h-3 text-wine/50 hidden md:block"
          fill="currentColor"
        />
        <Star
          className="absolute bottom-28 left-[18%] w-2.5 h-2.5 text-rose-soft/50 hidden md:block"
          fill="currentColor"
        />
        <Star
          className="absolute top-1/3 right-[7%] w-3.5 h-3.5 text-wine/50 hidden lg:block"
          fill="currentColor"
        />
        <Star
          className="absolute bottom-[22%] left-[38%] w-2.5 h-2.5 text-wine/50 hidden lg:block"
          fill="currentColor"
        />
      </div>

      <div className="container mx-auto px-6 relative z-10 max-w-7xl">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* RightContent — BookShowcase */}
          <div className="w-full lg:w-[50%] order-1 lg:order-2 flex justify-center items-center relative">
            {/* RadialGlow — foco atrás do livro */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[480px] h-[480px] rounded-full pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle at center, rgba(246,74,138,0.24) 0%, rgba(246,74,138,0.10) 42%, rgba(246,74,138,0) 75%)',
              }}
            />

            {/* ConcentricRings — moldura atrás do livro */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[380px] md:h-[380px] lg:w-[450px] lg:h-[450px] pointer-events-none hidden sm:block">
              {[1, 0.78, 0.56, 0.34].map((scale) => (
                <div
                  key={scale}
                  className="absolute top-1/2 left-1/2 rounded-full border border-[#f8bfd3]/40"
                  style={{
                    width: `${scale * 100}%`,
                    height: `${scale * 100}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              ))}
            </div>

            <motion.div
              style={{ y: yCard }}
              className="relative flex justify-center items-center w-[68%] max-w-[300px] sm:w-[60%] sm:max-w-[340px] lg:w-[78%] lg:max-w-[400px]"
            >
              {/* PedestalPlatform */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[85%] h-10 bg-gradient-to-r from-rose-soft/50 via-wine/35 to-rose-soft/50 blur-2xl rounded-full" />

              <motion.div
                animate={{ y: [0, -14, 0] }}
                transition={{
                  duration: 4.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                whileHover={{ rotate: -1.5, scale: 1.02 }}
                className={`relative z-20 w-full bg-white shadow-[0_30px_70px_rgba(216,44,114,0.15)] border border-pink-50 ${
                  isLivroCapa
                    ? 'p-3 rounded-[2rem]'
                    : 'p-4 rounded-[2.5rem] aspect-[4/5]'
                }`}
              >
                <div
                  className={`overflow-hidden relative bg-slate-50 ${
                    isLivroCapa
                      ? 'rounded-[1.5rem] aspect-[3/4]'
                      : 'w-full h-full rounded-[2rem]'
                  }`}
                >
                  <img
                    src={imagemFundo}
                    className="w-full h-full object-cover"
                    alt={isLivroCapa ? livroMes.title : 'Próxima edição'}
                  />

                  {isLivroCapa && evento?.vagas?.ultimasVagas && (
                    <div className="absolute bottom-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -rotate-12 pointer-events-none">
                      <div className="bg-gradient-to-r from-pink-500 via-rose-400 to-pink-500 text-white font-black uppercase tracking-widest px-40 py-3 shadow-2xl shadow-black/40 whitespace-nowrap text-xl">
                        ÚLTIMAS VAGAS!
                      </div>
                    </div>
                  )}

                  {isLivroCapa && (
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <p className="text-white text-[10px] font-black uppercase tracking-tight line-clamp-1">
                        {livroMes.title}
                      </p>
                      <p className="text-white/70 text-[8px] italic">
                        {livroMes.author}
                      </p>
                    </div>
                  )}
                </div>

                {/* BookMonthBadge */}
                <div className="absolute -right-2 -top-2 bg-wine text-white text-[9px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg shadow-wine/30">
                  {isLivroCapa ? livroMes.mesLabel : 'Vagas Limitadas'}
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* LeftContent */}
          <div className="w-full lg:w-[50%] order-2 lg:order-1 space-y-8 text-center lg:text-left flex flex-col items-center lg:items-start">
            <div className="space-y-5 w-full flex flex-col items-center lg:items-start">
              <div className="inline-flex items-center gap-2 text-wine font-black text-[10px] md:text-xs uppercase tracking-[0.35em]">
                <Sparkles size={12} className="text-rose-soft" />
                <span>Próxima Edição</span>
              </div>

              <h2 className="flex flex-col items-center lg:items-start">
                <span className="text-[60px] md:text-8xl lg:text-[104px] leading-[0.95] tracking-[0.03em] normal-case font-script text-ink">
                  Quero Participar
                </span>
                <span className="text-wine font-gilroy font-bold normal-case leading-tight tracking-[-0.02em] text-3xl md:text-4xl lg:text-5xl mt-2">
                  da edição de
                </span>
                <span className="relative inline-block mt-1">
                  <span className="text-wine normal-case font-script leading-none text-[64px] md:text-8xl lg:text-[108px]">
                    {mesNome}
                  </span>
                  <svg
                    viewBox="0 0 200 20"
                    preserveAspectRatio="none"
                    className="absolute -bottom-1 left-0 w-full h-4 text-wine"
                  >
                    <path
                      d="M2 12 Q 50 2, 100 10 T 198 8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </h2>

              <p className="text-slate-500 text-sm md:text-base max-w-md font-medium leading-relaxed">
                {description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-xl">
              <div className="flex items-center gap-4 bg-white p-5 rounded-3xl border border-pink-50 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="w-9 h-9 bg-blush-light rounded-xl flex items-center justify-center text-wine shrink-0">
                  <MapPin size={16} />
                </div>
                <div className="text-left">
                  <p className="text-[8px] uppercase text-slate-400 font-black tracking-widest">
                    Local
                  </p>
                  <p className="text-slate-900 text-sm font-black mt-0.5">
                    {location}
                  </p>
                </div>
              </div>

              {isLivroCapa && (
                <div
                  className={`flex items-center gap-4 p-5 rounded-3xl border transition-all relative ${
                    evento?.vagas?.ultimasVagas
                      ? 'bg-gradient-to-r from-rose-soft/20 to-wine/10 border-wine/40 shadow-md shadow-wine/20 hover:shadow-lg hover:-translate-y-0.5'
                      : 'bg-white border-pink-50 shadow-sm hover:shadow-md hover:-translate-y-0.5'
                  }`}
                >
                  <div className="w-9 h-9 bg-blush-light rounded-xl flex items-center justify-center text-wine shrink-0">
                    <BookOpen size={16} />
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-[8px] uppercase text-slate-400 font-black tracking-widest">
                      Livro do Mês
                    </p>
                    <p className="text-slate-900 text-sm font-black mt-0.5 line-clamp-1">
                      {livroMes.title}
                    </p>
                  </div>
                </div>
              )}

              {evento?.vagas?.ultimasVagas === true &&
                evento?.vagas?.esgotado !== true && (
                  <div className="flex justify-center lg:justify-start">
                    <UltimasVagasBadge
                      variant="solid"
                      vagasRestantes={evento?.vagas?.vagasRestantes || 0}
                    />
                  </div>
                )}
            </div>

            <motion.button
              onClick={() => navigate(`/evento/${evento.slug}/checkout`)}
              whileHover={{ y: -2, scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              className="group flex items-center justify-center gap-3 bg-gradient-to-br from-rose-soft via-wine to-wine-deep bg-[length:200%_200%] bg-[position:0%_50%] text-white px-8 sm:px-10 md:px-12 py-4 md:py-5 rounded-full font-black text-xs sm:text-sm uppercase tracking-[0.2em] shadow-xl shadow-wine/30 cursor-pointer transition-all duration-500 hover:shadow-2xl hover:shadow-wine/40 hover:bg-[position:100%_50%] w-full sm:w-auto"
            >
              Garantir minha vaga
              <ArrowRight
                size={16}
                className="stroke-[3] transition-transform duration-300 group-hover:translate-x-1"
              />
            </motion.button>

            {/* FeatureBadges */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-3 pt-1 w-full">
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                <Users size={14} className="text-wine" /> Encontros que conectam
              </span>
              <span className="hidden sm:block w-px h-4 bg-slate-200" />
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                <BookOpen size={14} className="text-wine" /> Literatura que
                transforma
              </span>
              <span className="hidden sm:block w-px h-4 bg-slate-200" />
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                <Sparkles size={14} className="text-wine" /> Comunidade que
                inspira
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NextEditionView;
